"use strict";

Object.defineProperty(exports, "__esModule", {
    value: true
});

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

var _GameModel = require("./GameModel");

var _GameModel2 = _interopRequireDefault(_GameModel);

var _GameLoader = require("./GameLoader");

var _GameLoader2 = _interopRequireDefault(_GameLoader);

var _Dispatcher = require("./Dispatcher");

var _Dispatcher2 = _interopRequireDefault(_Dispatcher);

var _GameService = require("./GameService");

var _GameService2 = _interopRequireDefault(_GameService);

var _utils = require("./utils");

var _flashBridge = require("./flashBridge");

var _flashBridge2 = _interopRequireDefault(_flashBridge);

var _GOSManager = require("./GOSManager");

var _GOSManager2 = _interopRequireDefault(_GOSManager);

var _eventsjs = require("@pch/eventsjs");

var _eventsjs2 = _interopRequireDefault(_eventsjs);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

var GOS_OPEN = "gosOpen";
var GOS_CREATE_PASS_SUCCESS = "gosCreatePassSuccess";
var GOS_FORFEIT = "gosForfeit";
var GOS_CONTINUE = "gosContinue";

var global = window;

/**
* @class
* @classdesc 
* GameManager handles the control flow of the games. It is the main interface between the parent site and the game.  
* The game manager should normally be the only thing you need to instantiate. 
*
* Each of the depenedencies have defaults, but can be replaced with your own implementation.
*
* You access the gameModel member for the game state while playing and for hooking into the
* onLoaded, onStarted, onEnded callbacks
*
* @mixes external:EventsMixin
*
* @param {GameJSON} gameJson - The game json config. This will be instantiated to a GameModel instance
*   which will house the current game state while the game is playing.
* @param {object} opts - The additional options/dependencies. Not all game types need all of the dependencies
*   @param {GameLoader} [opts.gameLoader=GameLoader] - GameLoader instance, replace if you have your own requirements for loading the game
*   @param {DispatcherInterface} [opts.dispatcher=Dispatcher] - An optional Dispatcher instance, defaults to PathDispatcher if it is available in the page
*   @param {GameService} [opts.service=GameService] - GameService instance
*   @param {string} [opts.serviceUri] - parameterized baseUrl for service requests. see {@link GameService#setRequestBodyTemplate} for vars available for interpolation 
*   @param {module:GameOverScreen~GameOverScreenInterface} [opts.gos] - optional GOS module, if none given, no GOS module is called
*   @param {HTMLElement|CssSelector|function} [opts.preloader] - an HTMLElement (or CSS selector representing elements) to .hide(), or a function to call
*   @param {object} [opts.user] - PCHUSER
*   @param {string} [opts.deviceType] - device type for game data
*
* @example 
*
*   var gm = new GameManager(IWGame.gameJson, {
*      gameLoader: new GameLoader(), // optional, inject your own game loader
*      dispatcher: PathDispacher, // optional, inject your own PathDispatcher, only needed for scratch games
*      service: new GameService(), // optional, inject your own game service
*      serviceUri: "/url/for/requests/{event}/{item.id}",
*      gos: GOS, // optional, inject your legacy (path V3) GOS module, or a GameOverScreen instance
*      preloader: $(".game-preloader") // optionally specify an element to be hidden on game load
*   });
*   gm.service.ajax = AjaxSimulator.ajax; // mock/replace the function used to make ajax requests
*   gm.init();
*   // game manager has pub/sub functionality if needed. Games can subscribe to events 
*   gm.on('someEvent', (arg1, arg2) => { ... }); 
*   // site can fire events, and vice versa 
*   gm.fireEvent("someEvent", "foo", "bar");
*
* @example 
*
*   // If you want to run multiple games without page load, you can call 
*   // This will clean up hooks to the gameProxy and reset the game over screen if it was provided.
*   myGameManger.dispose(); // dispose of the previous manager 
*   myGameManager = new GameManager(nextGameJson); // create your next game's manager as usual, same as above example
*/

var GameManager = function () {
    function GameManager(gameJson, opts) {
        _classCallCheck(this, GameManager);

        opts = opts || {};

        // add pub/sub methods, with on/off aliases as well
        (0, _utils.objectAssign)(this, _eventsjs2.default);
        /**
        * Aliases EventsMixin.addEventListener
        * @method
        * @see external:EventsMixin
        */
        this.on = this.addEventListener;
        /**
        * Aliases EventsMixin.removeEventListener
        * @method
        * @see external:EventsMixin
        */
        this.off = this.removeEventListener;

        /** @member {GameLoader} **/
        this.gameLoader = opts.gameLoader || new _GameLoader2.default();
        /** @member {DispatcherInterface} **/
        this.dispatcher = opts.dispatcher || _Dispatcher2.default;
        /** @member {GameService} **/
        this.service = opts.service || new _GameService2.default();
        /** @member {module:GOSManager~GOSManager} **/
        this.gosManager = (0, _GOSManager2.default)(opts.gos);
        this.preloader = opts.preloader;

        this.resetGame(gameJson, opts);

        this._onGOSOpen = this.onGOSOpen.bind(this);
        this.dispatcher.on(GOS_OPEN, this._onGOSOpen);
        this._onGOSCreatePassSuccess = this.onGOSCreatePassSuccess.bind(this);
        this.dispatcher.on(GOS_CREATE_PASS_SUCCESS, this._onGOSCreatePassSuccess);
        this._onGOSForfeit = this.onGOSForfeit.bind(this);
        this.dispatcher.on(GOS_FORFEIT, this._onGOSForfeit);

        /**
        * @member {object}
        */
        this.EVENTS = {
            /**
            * @event GameManager#gameLoad
            */
            gameloaded: 'gameLoad',
            /**
            * @event GameManager#gameStart
            * @type {object} 
            */
            start: 'gameStart',
            /**
            * @event GameManager#gameEnd
            * @type {object}
            */
            end: 'gameEnd',
            /**
            * @event GameManager#gameTechError
            */
            techerror: 'gameTechError',
            /**
            * @event GameManager#gameEvent
            */
            gameevent: 'gameEvent'
        };
    }

    /**
    * Convenience method for constructing the game manager data
    * from a raw iwe game info response and additonal options
    *
    * @param {object} data - the "data" block from a GetNextItem response, see docs http://pathgames-int.dev.pch.com/docs/v4/
    * @param {object} opts - Additional options
    *   @param {string} opts.serviceUri
    *   @param {object} opts.user - The PCHUSER object typically
    *   @param {string} opts.basePackageDomainOverride - for testing locally packages that rely on CORS that would be blocked otherwise, you can force the urls to be a local domain
    */


    _createClass(GameManager, [{
        key: "dispose",


        /**
        * Dispose this instanec
        * If you want to run multiple games without page load, use dispose to clean up hooks to the gameProxy and reset the game over screen if it was provided.
        *
        * @example 
        *   myGameManger.dispose(); // dispose of the previous manager 
        *   myGameManager = new GameManager(nextGameJson); // create your next game's manager as usual, same as above example
        */
        value: function dispose() {
            if (PCH.gameProxy.isCurrentManager(this)) {
                PCH.gameProxy.resetProxy();
            }
            this.gosManager.reset();
        }

        /**
        * Reset the game manager
        * Will instantiate a new gameModel and reset the onLoaded/Started/Ended states
        *
        * @param {GameJSON} gameJson - The game json representing the new game to reset to
        * @param {object} [opts]
        * @param {string} [opts.serviceUri]
        * @param {object} [opts.user]
        * @param {string} [opts.deviceType]
        */

    }, {
        key: "resetGame",
        value: function resetGame(gameJson) {
            var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

            this.gameModel = new _GameModel2.default(gameJson);
            this.gameModel.serviceUri = opts.serviceUri || this.gameModel.serviceUri;
            this.gameModel.user = opts.user || this.gameModel.user;
            if (this.gameModel.item) {
                this.gameModel.item.deviceType = (this.gameModel.item.deviceType || opts.deviceType || "desktop").toLowerCase();
            }
            this.service.baseUrl = this.gameModel.serviceUri;
            this.service.gameModel = this.gameModel;
            // listen for the important events
            this.gameModel.onLoaded.then(this.onGameLoaded.bind(this)).catch(this.onGameFailed.bind(this));
            this.gameModel.onStarted.then(this.onGameStarted.bind(this)).catch(this.onGameFailed.bind(this));
            this.gameModel.onEnded.then(this.onGameEnded.bind(this)).catch(this.onGameFailed.bind(this));
        }

        /**
        * Set the global PCH.gameProxy manager reference to this instance
        * You should not need to call this method directly, it is done when init() is called
        */

    }, {
        key: "setProxy",
        value: function setProxy() {
            PCH.gameProxy.setGameManager(this);
        }

        /**
        * Initialize and load the game. 
        * Will set the global PCH.gameProxy manager reference to this instance, so all calls
        * through gameProxy will run through this manager. Will 
        */

    }, {
        key: "init",
        value: function init() {
            var _this = this;

            this.gameLoader.loadGame(this.gameModel, function (err, resp) {
                _this.gameModel.setLoadedResponse(err, resp);
                // need to add a flash interface that the games all call
                if (_this.gameModel.isFlashGame) {
                    _flashBridge2.default.bind(_this);
                }
                _this.hidePreloader();
            });
            this.setProxy();
        }

        /**
        * Alias of init()
        */

    }, {
        key: "loadGame",
        value: function loadGame() {
            this.init();
        }

        /**
        * Hides the preloader element or calls the preloader function that was configured
        */

    }, {
        key: "hidePreloader",
        value: function hidePreloader() {
            if (!this.preloader) {
                return;
            }
            if (typeof this.preloader === "string") {
                var preloader = document.querySelector(this.preloader);
                if (preloader) {
                    preloader.style.display = "none";
                }
            } else if (this.preloader instanceof HTMLElement) {
                this.preloader.style.display = "none";
            } else if (this.preloader.hide && typeof this.preloader.hide === "function") {
                this.preloader.hide();
            }
        }

        /**
        * Callback to the game load
        *
        * @fires GameManager#gameLoad
        * @ignore
        */

    }, {
        key: "onGameLoaded",
        value: function onGameLoaded() {
            this.dispatcher.dispatch(this.EVENTS.gameloaded);
        }

        /**
        * Handler for when the game start promise resolves
        *
        * @fires GameManager#gameStart
        * @ignore
        */

    }, {
        key: "onGameStarted",
        value: function onGameStarted(startResponse) {
            //console.log("onGameStarted", startResponse);
            this.gameModel.updateSecurityTokenFromResponse(startResponse);
            this.gosManager.setup(this.gameModel, startResponse);
            this.dispatcher.dispatch(this.EVENTS.start, this.gameModel.startResponse.body);
            this.fireEvent(this.EVENTS.start, this.gameModel.startResponse.body);
        }

        /**
        * Handler for when a game promise is rejected
        *
        * @ignore
        */

    }, {
        key: "onGameFailed",
        value: function onGameFailed(errResponse) {
            console.log("onGameFailed", errResponse);
            this.gameModel.updateSecurityTokenFromResponse(errResponse);
            this.gosManager.setupGameFailed(this.gameModel, errResponse);
            this.dispatchEvent({ "event": "techerror" });
            this.fireEvent(this.EVENTS.techerror);
        }

        /**
        * Handler for when game end promise resolves
        *
        * @ignore
        */

    }, {
        key: "onGameEnded",
        value: function onGameEnded(endResponse) {
            //console.log("onGameEnded", endResponse);
            this.gameModel.updateSecurityTokenFromResponse(endResponse);
            this.gosManager.setupGameEnded(this.gameModel, endResponse);
            this.dispatcher.dispatch(this.EVENTS.end, endResponse.body);
            this.fireEvent(this.EVENTS.end, endResponse.body);
        }

        /**
        * @ignore
        * @deprecated
        */

    }, {
        key: "onGOSOpen",
        value: function onGOSOpen() {
            // to show last activity in uninav
            if (global.PCH && global.PCH.uniExp && global.PCH.uniExp.tokenCenter) {
                if (this.gos && typeof this.gos.isTokenWin === 'function' && this.gos.isTokenWin()) {
                    global.PCH.uniExp.tokenCenter.showLastActivity(this.gos.getActivityData());
                }
            }
            this.dispatcher.off(GOS_OPEN, this._onGOSOpen);
            this._onGOSOpen = null;
        }

        /**
        * Handles the dispatcher create pass event from GOS
        * Will send the complete event to the service
        *
        * @ignore
        */

    }, {
        key: "onGOSCreatePassSuccess",
        value: function onGOSCreatePassSuccess() {
            var _this2 = this;

            // if user is silver, this will listen to event client fires when they get
            // back a successful reg from RF and signal true game end
            this.service.complete(this.gameModel.getSecurityToken(), this.gameModel.getServiceData(), function (err, resp) {
                if (err) {
                    // handle
                }
                _this2.dispatcher.dispatch(GOS_CONTINUE);
            });
            this.dispatcher.off(GOS_CREATE_PASS_SUCCESS, this._onGOSCreatePassSuccess);
            this._onGOSCreatePassSuccess = null;
        }

        /**
        * Handles the dispatcher forfeit event from GOS
        * Will send the forfeit event to the service
        *
        * @ignore
        */

    }, {
        key: "onGOSForfeit",
        value: function onGOSForfeit() {
            var _this3 = this;

            //console.log("onGOSForfeit");
            this.service.forfeit(this.gameModel.getSecurityToken(), function (err, resp) {
                //console.log("forfeit response", resp);
                if (err) {
                    // handle
                }
                _this3.dispatcher.dispatch(GOS_FORFEIT);
            });
            this.dispatcher.off(GOS_FORFEIT, this._onGOSForfeit);
            this._onGOSForfeit = null;
        }

        /**
        * Make game start request
        *
        * @param {object} gameData - game data params to send in the request
        */

    }, {
        key: "startGame",
        value: function startGame(gameData) {
            var _this4 = this;

            this.service.start(this.gameModel.getSecurityToken(), this.gameModel.getGameId(), gameData, this.gameModel.getServiceData(), function (err, resp) {
                // treat starts as fail if the path is not "progress" indicating it could not be started
                if (resp && resp.body.path && resp.body.path.status && resp.body.path.status !== "progress") {
                    _this4.gameModel.setStartedResponse(_GameService.GameServiceError.createFromResponse(resp));
                } else {
                    _this4.gameModel.setStartedResponse(err, resp);
                }
            });
        }

        /**
        * Make game end request
        *
        * @param {object} gameData - game data params to send in the request
        */

    }, {
        key: "endGame",
        value: function endGame(gameData) {
            var _this5 = this;

            this.service.end(this.gameModel.getSecurityToken(), this.gameModel.getGameId(), gameData, this.gameModel.getServiceData(), function (err, resp) {
                _this5.gameModel.setEndedResponse(err, resp);
            });
        }

        /**
        * Send interrupt event to the game
        *
        * @param {*} args - Args to pass to the game
        */

    }, {
        key: "interruptGame",
        value: function interruptGame(args) {
            if (PCH.gameProxy.isCurrentManager(this)) {
                PCH.gameProxy.interruptGame(args);
                if (!this.gameModel.hasStarted && PCH.interruptAction) {
                    PCH.interruptAction();
                }
            }
        }

        /**
        * Make a request for the given event and params
        *
        * @param {string} eventName - "event" param value
        * @param {object} data - additional params
        * @param {GameService~requestCallback} callback - your callback to receive response
        */

    }, {
        key: "gameEvent",
        value: function gameEvent(eventName, data, callback) {
            this.fireEvent(this.EVENTS.gameevent, eventName, data);
            if (!this._ignoredEvents || !this._ignoredEvents.includes(eventName)) {
                return this.service.gameEvent(eventName, (0, _utils.objectAssign)({
                    "jtoken": this.gameModel.getSecurityToken(),
                    "gameid": this.gameModel.getGameId(),
                    "gamedata": data,
                    "apidata": this.gameModel.getServiceData()
                }, data || {}), callback);
            }
        }

        /**
        * Specify an event name to ignore from making service requests.
        * For example, if the game client is sending "error" events and you don't 
        * want those events making requests to the server. You can still
        * listen for these events by listening to the "gameevent" event 
        * (eg gameManager.addEventListener(gameManager.EVENTS.gameevent, function(eventName, data) { ... });
        *
        * You can call this method multiple times for multiple different event names
        *
        * @param {string|string[]}} eventName - the name of the event to ignore
        */

    }, {
        key: "ignoreGameEventRequests",
        value: function ignoreGameEventRequests(eventName) {
            this._ignoredEvents = this._ignoredEvents || [];
            if (!Array.isArray(eventName)) {
                eventName = [eventName];
            }
            for (var i = 0; i < eventName.length; i++) {
                if (!this._ignoredEvents.includes(eventName[i])) {
                    this._ignoredEvents.push(eventName[i]);
                }
            }
        }

        /**
        * Stop ignoring an event from making requests. Pass the event name
        * previously ignored using ignoreGameEventRequests()
        *
        * @param {string|string[]} eventName
        */

    }, {
        key: "unignoreGameEventRequests",
        value: function unignoreGameEventRequests(eventName) {
            if (this._ignoredEvents) {
                if (!Array.isArray(eventName)) {
                    eventName = [eventName];
                }
                this._ignoredEvents = this._ignoredEvents.filter(function (ev) {
                    return !eventName.includes(ev);
                });
            }
        }

        /**
        * Set a parameterized request body template, with param placeholders replaced
        * from user/game data.
        *
        * For more info on what local vars are available to the template refer to {@link GameService#setRequestBodyTemplate}
        *
        * @param {object} dataTemplate - The paramterized data, eg {"email": "{user.email}", "itemId": "{item.id}"}. For more info on what local vars are available to the template refer to {@link GameService#setRequestBodyTemplate}
        * @param {string} [eventName] - If given, this template will be used only when the request "event" value matches the given event name
        *
        * @see {@link GameService#setRequestBodyTemplate}
        * @see {@link GameService~RequestBodyTemplateContext}
        */

    }, {
        key: "setRequestBodyTemplate",
        value: function setRequestBodyTemplate(dataTemplate, eventName) {
            this.service.setRequestBodyTemplate(dataTemplate, eventName);
        }

        /**
        * Redirect the page to the configured game return url
        */

    }, {
        key: "redirect",
        value: function redirect() {
            window.location.href = this.gameModel.getReturnUrl();
        }

        /**
        * Prints error to the console
        */

    }, {
        key: "error",
        value: function error(msg) {
            console.error(msg);
        }

        /**
        * @deprecated
        * @ignore
        */

    }, {
        key: "dispatchEvent",
        value: function dispatchEvent(ev) {
            var eventName = ev.event;

            if (typeof this[eventName] === 'function') {
                this[eventName](ev);
            }

            if (this.EVENTS[eventName]) {
                this.dispatcher.dispatch(this.EVENTS[eventName], ev);
            } else {
                // capitalize first letter of custom event and prefix with 'game'
                this.dispatcher.dispatch('game' + eventName.charAt(0).toUpperCase() + eventName.slice(1), ev);
            }
        }

        /**
        * Add a listener to service request callbacks for a given event parameter value
        *
        * @param {string} eventName - The value to match for the "event" param in the request
        * @param {GameManager~requestEventCallback} callback - Your callback function to receive the response
        */

    }, {
        key: "addRequestEventListener",
        value: function addRequestEventListener(eventName, callback) {
            this.service.addEvent(this.service.EVENTS.REQUEST_CALLBACK, function (req, err, resp) {
                if (req.eventParamVal !== eventName) {
                    return;
                } else if (err) {
                    callback(err);
                } else {
                    callback(null, resp, req);
                }
            });
        }
    }], [{
        key: "constructGameJsonFromIWEResponse",
        value: function constructGameJsonFromIWEResponse(data, opts) {
            var initJson = (0, _utils.objectAssign)({}, data || {});
            opts = opts || {};

            initJson.game = initJson.item || initJson.game || {};
            initJson.game.path = initJson.path;
            initJson.serviceUri = opts.serviceUri;
            initJson.user = opts.user;

            // overwrite the base package url for local testing
            if (opts.basePackageDomainOverride) {
                initJson.game.base_package_url = initJson.game.base_package_url.replace(/^(https?:)?\/\/[^\/]+\/?/, opts.basePackageDomainOverride);
            }

            initJson.service = {
                data: {
                    token: initJson.security_token
                }
            };

            return initJson;
        }
    }]);

    return GameManager;
}();

/**
* Callback to request event listeners
*
* @callback GameManager~requestEventCallback
*
* @param {GameServiceError} [error] - first arg will be GameServiceError if failed, or null if not failed
* @param {GameServiceResponse} [resp] - service response
* @param {GameServiceRequest} [req] - service request that triggered the event
*/

exports.default = GameManager;